/**
 * Friendly copy for network-level fetch failures.
 *
 * fetch() rejects (rather than resolving with an error status) only when the
 * request never completed: offline, DNS failure, connection reset, server
 * unreachable, CORS. Browsers reject with jargon users otherwise see verbatim
 * in forms — "Failed to fetch" (Chrome), "Load failed" (Safari),
 * "NetworkError when attempting to fetch resource." (Firefox).
 *
 * Pure module (no import.meta.env) so it stays testable under jest — client.ts
 * itself can't be loaded there. Same pattern as lib/logContext.ts.
 */

export const NETWORK_ERROR_MESSAGE =
  "Couldn't reach the server. Check your connection and try again.";

/**
 * A fetch() rejection, carrying the friendly message every form renders
 * verbatim. Subclasses Error so existing `instanceof Error` handling is
 * unchanged; the distinct type lets main.tsx tell an exhausted-retry network
 * failure apart from an HTTP error once TanStack gives up (see networkFailure.ts).
 */
export class NetworkError extends Error {
  constructor(message: string = NETWORK_ERROR_MESSAGE) {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Map a fetch() rejection to a user-friendly Error. AbortErrors pass through
 * untouched so cancelled requests keep their cancellation semantics.
 */
export function toFriendlyNetworkError(err: unknown): Error {
  if (err instanceof Error && err.name === 'AbortError') return err;
  return new NetworkError();
}
